import React, { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import cloneDeep from 'lodash.clonedeep'
import { useRouter } from 'next/router'

import { usePagination } from '@hooks/usePagination'
import { useAuthentication } from '@hooks/useAuthentication'
import { useAlert } from '@hooks/useAlert'
import { BookByUserResponse } from '@models/book/BookByUserResponse'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { LikeSuccessStatusEnum } from '@models/book/LikeSuccessStatusEnum'
import { useTab } from '@components/Tabs/Tabs'
import { BookEnum } from '@interfaces/BookEnum'
import { scrollToElement } from '@lib/utils'
import { useBookAction } from '@hooks/book/useBookAction'
import { usePenNameAction } from '@hooks/penName/usePenNameAction'
import { SearchType, WritingTabAcceptProps, WritingTabProps } from './interface'

export function withWritingTab(Component: React.FC<WritingTabProps>) {
  function WithWritingTab({ userId }: WritingTabAcceptProps) {
    const bookClient = useBookAction()
    const router = useRouter()
    const penNameClient = usePenNameAction()
    const queryClient = useQueryClient()
    const alert = useAlert()
    const { isAuthenticated, canFetchApi } = useAuthentication()
    const loginModal = useModal({ modal: 'login' })
    const { page, perpage, pageChange, perpageChange } = usePagination()
    const { handleTabNameChange } = useTab()

    const [searchParam, setSearchParam] = useState<SearchType>({
      bookType: null,
      penNameId: null,
    })

    const { data: bookList, isLoading } = useQuery(
      ['books-by-user', userId, searchParam, page, perpage],
      () =>
        bookClient.getBooksByUserId({
          ...searchParam,
          id: userId,
          page,
          limitPerPage: perpage,
        }),
      {
        enabled: !!userId && canFetchApi,
        onSuccess: response => {
          if (response) {
            handleTabNameChange(
              'writing',
              `งานเขียน (${response.totalBook.toLocaleString()})`
            )
          }
        },
      }
    )

    const { data: penNameOptions = [] } = useQuery(
      ['pen-name-options', userId],
      () => penNameClient.getPenNameOptions(userId),
      { enabled: !!userId }
    )

    function handlePenNameChange(val: number) {
      pageChange(1)

      const { bookType, tab, slug } = router.query
      const query: any = { slug }
      if (bookType) query.bookType = bookType
      if (val) query.penNameId = val
      if (tab) query.tab = tab

      router.replace(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { scroll: false }
      )
    }

    function handleBookTypeChange(val: BookEnum) {
      pageChange(1)

      const { penNameId, tab, slug } = router.query
      const query: any = { slug }
      if (val) query.bookType = val
      if (penNameId) query.penNameId = penNameId
      if (tab) query.tab = tab

      router.replace(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { scroll: false }
      )
    }

    const { mutate: toggleBookLike, isLoading: isLoadingToggleBookLike } =
      useMutation((id: number) => bookClient.toggleBookLike(id), {
        onSuccess: (type, id) => {
          queryClient.setQueryData<BookByUserResponse | undefined>(
            ['books-by-user', userId, searchParam, page, perpage],
            (oldData?: BookByUserResponse) => {
              if (oldData) {
                const dataClone = cloneDeep(oldData)
                const books = dataClone.data
                const index = books.findIndex(item => item.id === id)
                books[index].isLiked = type === LikeSuccessStatusEnum.CREATE
                books[index].likeCount =
                  type === LikeSuccessStatusEnum.CREATE
                    ? books[index].likeCount + 1
                    : Math.max(0, books[index].likeCount - 1)

                return {
                  ...dataClone,
                  data: books,
                }
              }

              return oldData
            }
          )
        },
      })

    async function handleLikeBook(id: number) {
      if (isAuthenticated) {
        try {
          await toggleBookLike(id)
        } catch (_) {
          alert.error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง')
        }
      } else {
        loginModal.show()
      }
    }

    function handlePaginationChange(newPage: number) {
      pageChange(newPage)
      scrollToElement('#profile')
    }

    useEffect(() => {
      const bookType = (router.query.bookType as BookEnum) || null
      const penNameId = Number.isNaN(Number(router.query.penNameId))
        ? null
        : Number(router.query.penNameId)

      setSearchParam({
        bookType,
        penNameId,
      })
    }, [router.query.bookType, router.query.penNameId])

    const componentProps = {
      data: bookList?.data ?? [],
      penNameOptions,
      searchParam,
      page,
      perpage,
      total: bookList?.total || 0,
      isLoading: isLoading || !canFetchApi,
      isLoadingToggleBookLike,
      handlePaginationChange,
      perpageChange,
      handlePenNameChange,
      handleBookTypeChange,
      handleLikeBook,
    }
    return <Component {...componentProps} />
  }
  return WithWritingTab
}
